import { Invoice, InvoiceMap } from '@constants'
import { useApiCampaignCheckoutDisplayFields } from '@hooks-api/fortune-fastpass/useApiCampaignCheckoutDisplayFields'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { Input } from '@pets-ui/Input/Input'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { validateTaxID } from '@utils'
import { validateMobileBarcode } from '@utils/validateMobileBarcode'
import { useEffect, useRef } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { InvoiceOption } from './InvoiceOption'

const footer = stylex.create({
  root: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    backgroundColor: colors.black,
    color: colors.white,
  },
})

interface DialogInvoiceCategoryProps {
  open: boolean
  onClose: () => void
}

const DialogInvoiceCategoryContent = () => {
  const { campaignCode, countryCode, language } = useCheckout()
  const { control, setValue, formState: { errors } } = useFormContext<CheckoutOrder>()
  const [receiptType, phoneCarrierNumber, charityOrganization, businessUbn] = useWatch({ control, name: ['receipt_type', 'phone_carrier_number', 'charity_organization', 'business_ubn'] })

  const { destructuredData: checkoutFields } = useApiCampaignCheckoutDisplayFields(
    { code: campaignCode, country_code: countryCode, language },
  )

  const donateGroupOptions = Object.keys(checkoutFields?.donate_groups ?? {}).map(key => (
    <SelectOption value={key} key={key}>
      {checkoutFields?.donate_groups[key]}
    </SelectOption>
  ))

  const handleChangeInvoice = (value: Invoice) => {
    if (value !== Invoice.PHONE_CARRIER) {
      setValue('phone_carrier_number', '')
    }
    setValue('receipt_type', value)
  }

  return (
    <Box p={4}>
      <VStack gap={4}>
        <InvoiceOption
          value={Invoice.BUSINESS_EINVOICE}
          label={InvoiceMap.business_einvoice}
          checked={receiptType === Invoice.BUSINESS_EINVOICE}
          onChange={handleChangeInvoice}
        >
          <Controller
            control={control}
            name="business_ubn"
            rules={{
              required: '錯誤的資料格式',
              validate: value => validateTaxID(value) ? true : '錯誤的資料格式',
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                rootRef={ref}
                onChange={field.onChange}
                value={businessUbn}
                placeholder="請輸入統一編號"
                label="統一編號"
                error={!!error}
                helpText={error?.message}
                data-testid="input-business-ubn"
              />
            )}
          />
          <Controller
            control={control}
            name="business_ubn_title"
            rules={{
              required: '錯誤的資料格式',
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <HStack gap={1}>
                <Input
                  {...field}
                  rootRef={ref}
                  placeholder="請輸入營業人抬頭"
                  label="營業人抬頭"
                  fullWidth
                  error={!!error}
                  helpText={error?.message}
                  data-testid="input-business-ubn-title"
                  maxLength={30}
                />
              </HStack>
            )}
          />
        </InvoiceOption>
        <InvoiceOption
          value={Invoice.NON_BUSINESS_EINVOICE}
          label={InvoiceMap.non_business_einvoice}
          checked={receiptType === Invoice.NON_BUSINESS_EINVOICE}
          onChange={handleChangeInvoice}
        />
        <InvoiceOption
          value={Invoice.PHONE_CARRIER}
          label={InvoiceMap.phone_carrier}
          checked={receiptType === Invoice.PHONE_CARRIER}
          onChange={handleChangeInvoice}
        >
          <Controller
            control={control}
            name="phone_carrier_number"
            rules={{
              required: '必填欄位',
              validate: value => validateMobileBarcode(value) ? true : '請輸入完整後 7 碼：限數字 0-9、大寫英文 A-Z 及 + - .',
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                rootRef={ref}
                value={phoneCarrierNumber}
                placeholder="請輸入手機條碼"
                error={!!error}
                helpText={error?.message}
              />
            )}
          />
        </InvoiceOption>
        <InvoiceOption
          value={Invoice.DONATE_INVOICE}
          label={InvoiceMap.donate_invoice}
          checked={receiptType === Invoice.DONATE_INVOICE}
          onChange={handleChangeInvoice}
        >
          <Controller
            control={control}
            name="charity_organization"
            rules={{ required: '此為必填欄位' }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Select
                {...field}
                value={charityOrganization}
                rootRef={ref}
                error={!!error}
                helpText={error?.message}
                data-testid="select-charity-organization"
              >
                {donateGroupOptions}
              </Select>
            )}
          />
        </InvoiceOption>
        {errors.receipt_type && (
          <Text color={colors.red600}>
            {errors.receipt_type.message}
          </Text>
        )}
      </VStack>
    </Box>
  )
}
interface InvoiceCategoryObject {
  receiptType: CheckoutOrder['receipt_type']
  businessUbn: CheckoutOrder['business_ubn']
  businessUbnTitle: CheckoutOrder['business_ubn_title']
  phoneCarrierNumber: CheckoutOrder['phone_carrier_number']
  charityOrganization: CheckoutOrder['charity_organization']
}

export function DialogInvoiceCategory({ open, onClose }: DialogInvoiceCategoryProps) {
  const { setValue, trigger, formState: { errors }, getValues } = useFormContext<CheckoutOrder>()
  const isMobile = useResponsive('mobile')
  const invoiceCategoryObjectRef = useRef<InvoiceCategoryObject | null>(null)

  useEffect(() => {
    if (!open) return
    const { receipt_type, business_ubn, business_ubn_title, phone_carrier_number, charity_organization } = getValues()
    const saveOriginalInvoiceCategory = () => {
      invoiceCategoryObjectRef.current = {
        receiptType: receipt_type,
        businessUbn: business_ubn,
        businessUbnTitle: business_ubn_title,
        phoneCarrierNumber: phone_carrier_number,
        charityOrganization: charity_organization,
      }
    }
    saveOriginalInvoiceCategory()
  }, [getValues, open])

  const handleReset = () => {
    if (!invoiceCategoryObjectRef.current) return
    setValue('receipt_type', invoiceCategoryObjectRef.current.receiptType)
    setValue('business_ubn', invoiceCategoryObjectRef.current.businessUbn)
    setValue('business_ubn_title', invoiceCategoryObjectRef.current.businessUbnTitle)
    setValue('phone_carrier_number', invoiceCategoryObjectRef.current.phoneCarrierNumber)
    setValue('charity_organization', invoiceCategoryObjectRef.current.charityOrganization)
  }

  const handleSubmit = async () => {
    await trigger([
      'receipt_type',
      'business_ubn_title',
      'business_ubn',
      'phone_carrier_number',
      'charity_organization',
    ])
    const isError = errors.receipt_type
      || errors.business_ubn_title
      || errors.business_ubn
      || errors.phone_carrier_number
      || errors.charity_organization
    if (isError) return
    onClose()
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  return (
    <ResponsiveDialog
      open={open}
      fullWidth
    >
      <DialogHeader title="發票種類" onClose={handleClose} />
      <VStack gap={4}>
        <DialogInvoiceCategoryContent />
      </VStack>
      {/** Footer */}
      <HStack rootStyle={footer.root} alignItems="center" gap={2}>
        <Button variant="filled" rootStyle={footer.button} onClick={handleSubmit} fullWidth={isMobile}>
          確認
        </Button>
      </HStack>
    </ResponsiveDialog>
  )
}
